import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3, Calendar, FileDown, FileSpreadsheet, Package, Users, Ticket,
  XCircle, Calculator, PieChart, Layers, TrendingUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useGroupSalesReport } from "@/hooks/useGroupSalesReport";
import jsPDF from "jspdf";

const reportNavItems = [
  { title: "Vendas", url: "/dashboard/relatorios", icon: BarChart3, description: "Análise de vendas" },
  { title: "DRE", url: "/dashboard/dre", icon: FileSpreadsheet, description: "Demonstrativo de resultado" },
  { title: "Combos", url: "/dashboard/relatorio-combos", icon: Layers, description: "Vendas de combos" },
  { title: "Ticket Médio", url: "/dashboard/ticket-medio", icon: Users, description: "Média por pessoa" },
  { title: "Entradas", url: "/dashboard/relatorio-entradas", icon: Ticket, description: "Couvert e entradas" },
  { title: "Cancelamentos", url: "/dashboard/cancelamentos", icon: XCircle, description: "Itens cancelados" },
  { title: "CMV", url: "/dashboard/cmv", icon: Calculator, description: "Custo de mercadoria" },
  { title: "Margens", url: "/dashboard/margens", icon: PieChart, description: "Lucro por produto" },
  { title: "Por Grupo", url: "/dashboard/relatorio-grupo", icon: Package, description: "Vendas por categoria" },
];

type DatePreset = "today" | "7days" | "30days" | "thisMonth" | "custom";

export default function RelatorioGrupoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRestaurant } = useRestaurantContext();

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>("30days");
  const [dateFrom, setDateFrom] = useState<Date>(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState<Date>(new Date());

  const { data: categories } = useQuery({
    queryKey: ["categories", activeRestaurant?.id],
    queryFn: async () => {
      if (!activeRestaurant?.id) return [];
      const { data, error } = await supabase
        .from("categories")
        .select("id, name")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!activeRestaurant?.id,
  });

  const { data: report, isLoading } = useGroupSalesReport({ categoryId, dateFrom, dateTo });

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    const now = new Date();
    switch (preset) {
      case "today": setDateFrom(now); setDateTo(now); break;
      case "7days": setDateFrom(subDays(now, 7)); setDateTo(now); break;
      case "30days": setDateFrom(subDays(now, 30)); setDateTo(now); break;
      case "thisMonth": setDateFrom(startOfMonth(now)); setDateTo(endOfMonth(now)); break;
    }
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

  const selectedCategoryName = categories?.find((c) => c.id === categoryId)?.name || "";

  const handleExportPDF = () => {
    if (!report || report.items.length === 0) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Vendas por Grupo: ${selectedCategoryName}`, 14, 20);
    doc.setFontSize(10);
    doc.text(
      `Período: ${format(dateFrom, "dd/MM/yyyy")} a ${format(dateTo, "dd/MM/yyyy")}`,
      14, 28,
    );

    let y = 40;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Produto", 14, y);
    doc.text("Qtd", 120, y, { align: "right" });
    doc.text("Valor Total", 190, y, { align: "right" });
    y += 2;
    doc.line(14, y, 196, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    for (const item of report.items) {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(item.productName, 14, y);
      doc.text(String(item.quantity), 120, y, { align: "right" });
      doc.text(formatCurrency(item.totalValue), 190, y, { align: "right" });
      y += 7;
    }

    y += 2;
    doc.line(14, y, 196, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL", 14, y);
    doc.text(String(report.totalQuantity), 120, y, { align: "right" });
    doc.text(formatCurrency(report.totalValue), 190, y, { align: "right" });

    doc.save(`vendas-grupo-${selectedCategoryName}.pdf`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-warning" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Vendas por Grupo</h1>
            <p className="text-sm text-muted-foreground">Vendas detalhadas por categoria de produto</p>
          </div>
        </div>
      </div>

      {/* Report Navigation Hub */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
        {reportNavItems.map((item) => {
          const isCurrentPage = location.pathname === item.url;
          return (
            <Card
              key={item.url}
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                isCurrentPage
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "hover:border-primary/50",
              )}
              onClick={() => !isCurrentPage && navigate(item.url)}
            >
              <CardContent className="p-4 flex flex-col items-center gap-2 text-center">
                <item.icon className={cn("h-6 w-6", isCurrentPage ? "text-primary" : "text-muted-foreground")} />
                <span className={cn("text-sm font-medium", isCurrentPage && "text-primary")}>{item.title}</span>
                {!isCurrentPage && (
                  <span className="text-xs text-muted-foreground hidden sm:block">{item.description}</span>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={categoryId || ""} onValueChange={(v) => setCategoryId(v)}>
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder="Selecione uma categoria" />
          </SelectTrigger>
          <SelectContent>
            {categories?.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(["today", "7days", "30days", "thisMonth"] as DatePreset[]).map((preset) => (
          <Button
            key={preset}
            variant={datePreset === preset ? "default" : "outline"}
            size="sm"
            onClick={() => handlePresetChange(preset)}
          >
            {preset === "today" && "Hoje"}
            {preset === "7days" && "7 dias"}
            {preset === "30days" && "30 dias"}
            {preset === "thisMonth" && "Este mês"}
          </Button>
        ))}

        <Popover>
          <PopoverTrigger asChild>
            <Button variant={datePreset === "custom" ? "default" : "outline"} size="sm" className="gap-2">
              <Calendar className="h-4 w-4" />
              Personalizado
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex">
              <div className="border-r p-3">
                <p className="text-sm font-medium mb-2">De</p>
                <CalendarComponent
                  mode="single"
                  selected={dateFrom}
                  onSelect={(date) => { if (date) { setDateFrom(date); setDatePreset("custom"); } }}
                  locale={ptBR}
                />
              </div>
              <div className="p-3">
                <p className="text-sm font-medium mb-2">Até</p>
                <CalendarComponent
                  mode="single"
                  selected={dateTo}
                  onSelect={(date) => { if (date) { setDateTo(date); setDatePreset("custom"); } }}
                  locale={ptBR}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportPDF}
          disabled={!report || report.items.length === 0}
          className="gap-2"
        >
          <FileDown className="h-4 w-4" />
          PDF
        </Button>
      </div>

      {/* Period label */}
      <p className="text-sm text-muted-foreground">
        Período: {format(dateFrom, "dd/MM/yyyy", { locale: ptBR })} a {format(dateTo, "dd/MM/yyyy", { locale: ptBR })}
      </p>

      {/* Results */}
      {!categoryId ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            Selecione uma categoria para visualizar as vendas.
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      ) : report && report.items.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Produtos vendidos — {selectedCategoryName}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead className="text-right">Valor Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.items.map((item) => (
                  <TableRow key={item.productId}>
                    <TableCell className="font-medium">{item.productName}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-bold">Total</TableCell>
                  <TableCell className="text-right font-bold">{report.totalQuantity}</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(report.totalValue)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            Nenhuma venda encontrada para esta categoria no período selecionado.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
